import { zodCustomErrorsConst } from "@/resources";
import { RequestCompanyType } from "@/types/busrequest";
import {
  zodAddressWithLocation,
  zodBusRequestAddressWithLocation,
} from "@/types/location";
import { isAfter } from "date-fns";
import { isBefore } from "date-fns/isBefore";
import { TFunction } from "i18next";
import { z } from "zod/v4";

export type BusrequestFormSchema = z.infer<typeof busrequestFormSchema>;

const requestCompanyType = z.enum([
  "SCHOOL",
  "CLUB",
  "COMPANY",
  "AUTHORITY",
  "PRIVATE",
]);

// Aus zod kopiert und das + optional gemacht
const phoneRegex = /^\+?(?:[0-9]){6,14}[0-9]$/;

const outwardDateSchema = z.date().refine(
  (data) => {
    if (isBefore(data, new Date())) {
      return false;
    }
    return true;
  },
  {
    params: {
      i18n: zodCustomErrorsConst.FutureDateRequired,
    },
  },
);

const returnDateSchema = z.date().refine(
  (data) => {
    if (isBefore(data, new Date())) {
      return false;
    }
    return true;
  },
  {
    params: {
      i18n: zodCustomErrorsConst.FutureDateRequired,
    },
  },
);

const phoneSchema = z
  .string()
  .optional()
  .refine((data) => (data ? phoneRegex.test(data) : true), {
    params: {
      i18n: zodCustomErrorsConst.Telephone,
    },
  });

const dsgvoSchema = z.boolean().refine((data) => !!data, {
  params: {
    i18n: zodCustomErrorsConst.DataProtectionAgreement,
  },
});

const selectedCompaniesSchema = z.array(z.string()).refine(
  (data) => {
    return data.length > 0;
  },
  {
    params: {
      i18n: zodCustomErrorsConst.SelectedCompaniesRequired,
    },
  },
);

const busrequestBaseFields = z.object({
  outwardDate: outwardDateSchema,
  from: zodBusRequestAddressWithLocation,
  to: zodBusRequestAddressWithLocation,
  expanded: z.boolean(),
  passengers: z.number().min(6).max(1000),
  description: z.string().optional(),
  companyType: requestCompanyType,
  firstName: z.string(),
  lastName: z.string(),
  email: z.email(),
  phone: phoneSchema,
  address: zodAddressWithLocation.optional(),
  companyName: z.string().optional(),
  dsgvo: dsgvoSchema,
});

const requestOneWaySchemaCollective = z.object({
  ...busrequestBaseFields.shape,
  transferType: z.literal("transferoneway"),
  requestTarget: z.literal("collective"),
  selectedCompanies: z.array(z.string()),
});

const requestOneWaySchemaIndividual = z.object({
  ...busrequestBaseFields.shape,
  transferType: z.literal("transferoneway"),
  requestTarget: z.literal("individual"),
  selectedCompanies: selectedCompaniesSchema,
});

const requestOneWaySchema = z.discriminatedUnion("requestTarget", [
  requestOneWaySchemaCollective,
  requestOneWaySchemaIndividual,
]);

const requestTwoWaySchemaCollective = z.object({
  ...busrequestBaseFields.shape,
  transferType: z.literal("transfertwoway"),
  returnDate: returnDateSchema,
  requestTarget: z.literal("collective"),
  selectedCompanies: z.array(z.string()),
});

const requestTwoWaySchemaIndividual = z.object({
  ...busrequestBaseFields.shape,
  transferType: z.literal("transfertwoway"),
  returnDate: returnDateSchema,
  requestTarget: z.literal("individual"),
  selectedCompanies: selectedCompaniesSchema,
});

const requestTwoWaySchema = z
  .discriminatedUnion("requestTarget", [
    requestTwoWaySchemaCollective,
    requestTwoWaySchemaIndividual,
  ])
  .refine((data) => isAfter(data.returnDate, data.outwardDate), {
    params: {
      i18n: {
        key: zodCustomErrorsConst.DateAfterDate,
        values: {
          date_key: "zodBusrequestPaths:outwardDate",
        },
      },
    },
    path: ["returnDate"],
  })
  .refine((data) => isAfter(data.returnDate, data.outwardDate), {
    params: {
      i18n: {
        key: zodCustomErrorsConst.DateBeforeDate,
        values: {
          date_key: "zodBusrequestPaths:returnDate",
        },
      },
    },
    path: ["outwardDate"],
  });

const requestTourSchemaCollective = z.object({
  ...busrequestBaseFields.shape,
  transferType: z.literal("transfertour"),
  returnDate: returnDateSchema,
  requestTarget: z.literal("collective"),
  selectedCompanies: z.array(z.string()),
});

const requestTourSchemaIndividual = z.object({
  ...busrequestBaseFields.shape,
  transferType: z.literal("transfertour"),
  returnDate: returnDateSchema,
  requestTarget: z.literal("individual"),
  selectedCompanies: selectedCompaniesSchema,
});

const requestTourSchema = z
  .discriminatedUnion("requestTarget", [
    requestTourSchemaCollective,
    requestTourSchemaIndividual,
  ])
  .refine((data) => isAfter(data.returnDate, data.outwardDate), {
    params: {
      i18n: {
        key: zodCustomErrorsConst.DateAfterDate,
        values: {
          date_key: "zodBusrequestPaths:outwardDateTour",
        },
      },
    },
    path: ["returnDate"],
  })
  .refine((data) => isAfter(data.returnDate, data.outwardDate), {
    params: {
      i18n: {
        key: zodCustomErrorsConst.DateBeforeDate,
        values: {
          date_key: "zodBusrequestPaths:returnDateTour",
        },
      },
    },
    path: ["outwardDate"],
  });

export const busrequestFormSchema = z.discriminatedUnion("transferType", [
  requestOneWaySchema,
  requestTwoWaySchema,
  requestTourSchema,
]);

export function calcCompanyTypeLabel(
  companyType: RequestCompanyType,
  t: TFunction,
): string {
  switch (companyType) {
    case "AUTHORITY": {
      return (
        t("MAIN.SINGLEREQUEST.COMPANYTYPE.LABEL.FIRST.MASKULIN") +
        t("PROVIDER.COMPANYTYPE.AUTHORITY.TEXT")
      );
    }
    case "CLUB": {
      return (
        t("MAIN.SINGLEREQUEST.COMPANYTYPE.LABEL.FIRST.NEUTRUM") +
        t("PROVIDER.COMPANYTYPE.CLUB.TEXT") +
        t("MAIN.SINGLEREQUEST.COMPANYTYPE.LABEL.SECOND")
      );
    }
    case "COMPANY": {
      return (
        t("MAIN.SINGLEREQUEST.COMPANYTYPE.LABEL.FIRST.MASKULIN") +
        t("PROVIDER.COMPANYTYPE.COMPANY.TEXT")
      );
    }
    case "SCHOOL": {
      return (
        t("MAIN.SINGLEREQUEST.COMPANYTYPE.LABEL.FIRST.MASKULIN") +
        t("PROVIDER.COMPANYTYPE.SCHOOL.TEXT")
      );
    }
    default: {
      return "";
    }
  }
}
